/**
 * stopCampaign — RPC contract for the callable that changes what a campaign's
 * ad is doing at Meta: pause it, resume it, or cancel the run outright.
 *
 * The operator who cannot use Ads Manager is the whole premise of this
 * feature, so a product that can start spending and not stop it defeats
 * itself. This is the door that answers it.
 *
 * ONE CONTRACT CARRYING A VERB, NOT THREE. The three intents name the same
 * campaign, take the same two ids, answer with the same shape, and are gated
 * by the same authority — three files whose only difference is one literal
 * would be three names for one job. `resume` sits under a door named `stop`
 * because it has no other origin: a campaign is resumable only because this
 * same door paused it, and a separate `resumeCampaign` would be a second
 * lever onto one state, free to disagree with the first about which states it
 * may act from.
 *
 * The verb is a plain enum rather than a discriminated union because it
 * decides no other field: no intent carries a payload the others do not, so a
 * union would buy variants that differ in nothing.
 *
 * Cancel is not pause. A pause writes `PAUSED` to the ad and leaves the tree
 * standing; a cancel additionally archives the campaign at Meta and stamps
 * the run ended, which is the one intent nothing resumes from.
 *
 * The input names the campaign and carries no part of its document — the
 * constraint `launchCampaign.action.ts` states, holding for the same reason
 * one door over. Whether an intent can act at all turns on where the campaign
 * stands and on the ad ids the build wrote, and a caller who could hand over
 * that shape would be answering the question the callable is asking. The
 * server reads the document; the caller names it.
 *
 * Fields justified:
 *   eventId    — the event this campaign promotes; the path is
 *                workspaces/{orgId}/events/{eventId}/campaigns/{campaignId}.
 *   campaignId — the campaign whose delivery changes.
 *   intent     — which of the three the operator pressed.
 * Excluded:
 *   status, metaIds, metaStatus — every one on the document, read there.
 *   a reason or note for the stop — the campaign records that the spend
 *           stopped and when; why the operator stopped it has no reader in
 *           this product, and an unread field does not exist.
 *   an idempotency key — each intent asserts a state rather than moving a
 *           counter, so a redelivery lands the state it already found.
 *
 * Output: a discriminated union on `outcome`.
 *
 * ITS REFUSALS SHARE NO TYPE WITH THE OTHER TWO DOORS, and should not. A
 * proposal is refused by the world it would be composed against, a
 * confirmation by a world that has moved since it was, and a stop by where
 * this campaign itself stands and by whether the ad network took the write.
 * The three sets do not overlap in a single member; one vocabulary spanning
 * them would fit none of them and would grow a member every time any one door
 * learned a new way to say no.
 *
 * The success member carries nothing: the campaign's own status and
 * `stoppedAt` land on the document the console is already subscribed to, and
 * a copy on the wire would be a second representation of one fact.
 *
 * Members:
 *   applied            — Meta holds the delivery state the operator asked
 *                        for, and the campaign records it. The same answer
 *                        for a redelivery that found it already there, since
 *                        each intent asserts a state rather than a change.
 *   intentDoesNotApply — the campaign is not somewhere this intent can act
 *                        from: nothing has been built yet to pause, or the
 *                        run is already over. Nothing travels with it — the
 *                        console holds the status live and is the surface
 *                        that renders the sentence.
 *   notAppliedAtMeta   — the write did not land at the ad network, whether it
 *                        was refused or the network was never reached. The
 *                        one thing this has to say is that the spend is
 *                        UNCHANGED, which is the invariant the whole door
 *                        exists for. The provider's own words do not travel:
 *                        the remedy is the same for every failure bucket —
 *                        press it again — and a credential that has died
 *                        surfaces on the integration panel that owns it.
 *
 * Organization context is explicit in the request. The server validates
 * membership or possession of the scoped access token independently.
 */
import { z } from "zod";
import { Id } from "./workspace.entity.ts";

export const StopIntentSchema = z
	.enum(["pause", "resume", "cancel"])
	.describe(
		"what the operator is asking of this campaign's delivery — pause holds the spend and leaves the ad tree standing, resume releases a hold this same door placed, cancel ends the run and archives the campaign at Meta",
	);
export type StopIntent = z.infer<typeof StopIntentSchema>;

export const StopCampaignInput = z
	.object({
		orgId: Id,
		eventId: Id.describe("the event this campaign promotes"),
		campaignId: Id.describe("the campaign whose delivery changes"),
		intent: StopIntentSchema,
	})
	.strict();
export type StopCampaignInput = z.infer<typeof StopCampaignInput>;

export const StopCampaignOutput = z.discriminatedUnion("outcome", [
	z.object({ outcome: z.literal("applied") }).strict(),
	z.object({ outcome: z.literal("intentDoesNotApply") }).strict(),
	z.object({ outcome: z.literal("notAppliedAtMeta") }).strict(),
]);
export type StopCampaignOutput = z.infer<typeof StopCampaignOutput>;
